import type { AppContext, ViewCleanup } from "../app";
import { groupByRegion, loadCountries, REGION_ORDER } from "../data/countries";
import { loadState, saveState } from "../state/storage";
import { pullLeaderboard } from "../state/sync";
import type { ArcadeMode, LeaderboardEntry } from "../state/types";

// Global leaderboard view. Renders instantly from the local cache when
// available, then refreshes from the network in the background. If the
// network call fails (offline, free tier shut down, whatever), the
// cached results keep working.

const MODES: { value: ArcadeMode; label: string; unit: string }[] = [
  { value: "speed-run", label: "Speed run", unit: "correct in 60s" },
  { value: "survival", label: "Survival", unit: "longest streak" },
  { value: "reverse", label: "Reverse", unit: "out of 10" },
];

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtAge = (ms: number): string => {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const cacheKey = (mode: ArcadeMode, region: string): string =>
  `${mode}:${region}`;

export const mountLeaderboard = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  root.classList.add("view-leaderboard");
  ctx.globe.setTargetIso(null);
  ctx.globe.setHoverEnabled(true);

  const table = await loadCountries();
  const grouped = groupByRegion(table);
  const populatedRegions = REGION_ORDER.filter(
    (r) => (grouped[r] ?? []).length > 0,
  );

  let state = loadState();

  const initialMode =
    (root.dataset.mode as ArcadeMode | undefined) ?? "speed-run";
  const initialRegion =
    (root.dataset.region && populatedRegions.includes(root.dataset.region))
      ? root.dataset.region
      : populatedRegions[0]!;

  const card = document.createElement("section");
  card.className = "leaderboard-card";
  card.innerHTML = `
    <header class="leaderboard-header">
      <button type="button" class="leaderboard-back" aria-label="Back">← Back</button>
      <h1>Global leaderboard</h1>
    </header>
    <div class="leaderboard-controls">
      <label class="leaderboard-control">
        <span class="leaderboard-control-label">Mode</span>
        <select class="leaderboard-mode"></select>
      </label>
      <label class="leaderboard-control">
        <span class="leaderboard-control-label">Region</span>
        <select class="leaderboard-region"></select>
      </label>
    </div>
    <p class="leaderboard-meta" aria-live="polite"></p>
    <ol class="leaderboard-list"></ol>
    <p class="leaderboard-empty" hidden>No scores yet. Be the first.</p>
  `;
  root.appendChild(card);

  const back = card.querySelector(".leaderboard-back") as HTMLButtonElement;
  const modeSelect = card.querySelector(".leaderboard-mode") as HTMLSelectElement;
  const regionSelect = card.querySelector(".leaderboard-region") as HTMLSelectElement;
  const list = card.querySelector(".leaderboard-list") as HTMLOListElement;
  const meta = card.querySelector(".leaderboard-meta") as HTMLParagraphElement;
  const empty = card.querySelector(".leaderboard-empty") as HTMLParagraphElement;

  for (const m of MODES) {
    const opt = document.createElement("option");
    opt.value = m.value;
    opt.textContent = m.label;
    modeSelect.appendChild(opt);
  }
  modeSelect.value = initialMode;

  for (const region of populatedRegions) {
    const opt = document.createElement("option");
    opt.value = region;
    opt.textContent = region;
    regionSelect.appendChild(opt);
  }
  regionSelect.value = initialRegion;

  let currentMode: ArcadeMode = initialMode;
  let currentRegion = initialRegion;
  let aborted = false;
  let inflight = 0;

  const renderEntries = (
    entries: LeaderboardEntry[],
    source: "cache" | "network",
    fetchedAt: number | null,
  ): void => {
    list.replaceChildren();
    if (entries.length === 0) {
      empty.hidden = false;
      meta.textContent = "";
      return;
    }
    empty.hidden = true;
    const modeMeta = MODES.find((m) => m.value === currentMode);
    const unit = modeMeta ? modeMeta.unit : "score";
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i]!;
      const li = document.createElement("li");
      li.className = "leaderboard-row";
      if (entry.username === state.username) li.classList.add("is-self");
      li.innerHTML = `
        <span class="leaderboard-rank">${i + 1}</span>
        <span class="leaderboard-name">${escapeHtml(entry.username)}</span>
        <span class="leaderboard-score">${entry.score}</span>
      `;
      list.appendChild(li);
    }
    if (source === "cache" && fetchedAt) {
      meta.textContent = `Showing cached results from ${fmtAge(fetchedAt)} · ${unit}`;
    } else if (source === "network") {
      meta.textContent = `Updated just now · ${unit}`;
    } else {
      meta.textContent = unit;
    }
  };

  const refresh = async (): Promise<void> => {
    const myFetch = ++inflight;
    const mode = currentMode;
    const region = currentRegion;
    const key = cacheKey(mode, region);

    // Synchronous: render from cache immediately if available.
    const cached = state.cachedLeaderboards[key];
    if (cached) {
      renderEntries(cached.entries, "cache", cached.fetchedAt);
    } else {
      list.replaceChildren();
      empty.hidden = true;
      meta.textContent = "Loading…";
    }

    // Async refresh from server.
    const fresh = await pullLeaderboard(mode, region);
    if (aborted || myFetch !== inflight) return;
    if (fresh === null) {
      // Network failed. Keep whatever we rendered from cache; surface
      // status if there was no cache to fall back on.
      if (!cached) {
        meta.textContent = "Couldn't reach the server. Try again later.";
      }
      return;
    }
    const fetchedAt = Date.now();
    state = {
      ...state,
      cachedLeaderboards: {
        ...state.cachedLeaderboards,
        [key]: { entries: fresh, fetchedAt },
      },
    };
    saveState(state);
    renderEntries(fresh, "network", fetchedAt);
  };

  const onModeChange = (): void => {
    currentMode = modeSelect.value as ArcadeMode;
    void refresh();
  };
  const onRegionChange = (): void => {
    currentRegion = regionSelect.value;
    void refresh();
  };
  const onBack = (): void => ctx.nav.goArcade();

  modeSelect.addEventListener("change", onModeChange);
  regionSelect.addEventListener("change", onRegionChange);
  back.addEventListener("click", onBack);

  void refresh();

  return () => {
    aborted = true;
    modeSelect.removeEventListener("change", onModeChange);
    regionSelect.removeEventListener("change", onRegionChange);
    back.removeEventListener("click", onBack);
    root.classList.remove("view-leaderboard");
  };
};
